import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from "@angular/core";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import {
  Contact,
  ContactDTO,
  createDefaultContact,
  Day,
  ThiefProtectionConfig,
  ThiefProtectionConfigCreate,
  ThiefProtectionConfigUpdate,
} from "../../../../../models/api/thiefProtection";
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SelectModule } from "@openng/optimus-ui/select";
import { DropdownOption } from "../../../../../models/ui/dropdownOption";
import { InputNumberModule } from "@openng/optimus-ui/inputnumber";
import { DatePickerModule } from "@openng/optimus-ui/datepicker";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { SvgIconComponent } from "../../../../../shared/components/svg-icon/svg-icon.component";
import { DateTime } from "luxon";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { TIMEZONES } from "../../../../../constants/timezones";
import { ThiefProtectionService } from "../../../../../services/api/thief-protection/thief-protection.service";
import { EMAIL_PATTERN } from "../../../../../constants/regExp";
import { AssetHierarchyDTO } from "../../../../../models/api/asset/assetHierarchyDTO";
import { MessageService } from "@openng/optimus-ui/api";

interface TimeWindowFormGroup {
  startTime: FormControl<Date | null>;
  endTime: FormControl<Date | null>;
}

interface ContactFormGroup {
  email: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  type: FormControl<"PRIMARY" | "ADDITIONAL">;
  level: FormControl<number>;
  lang: FormControl<"de" | "en">;
}

@Component({
  selector: "app-building-open-times",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    SelectModule,
    InputNumberModule,
    InputTextModule,
    InputNumberModule,
    DatePickerModule,
    FormsModule,
    SvgIconComponent,
    ButtonComponent,
  ],
  templateUrl: "./building-open-times.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./building-open-times.component.scss",
})
export class BuildingOpenTimesComponent implements OnInit {
  private config = inject(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);
  private thiefProtectionService = inject(ThiefProtectionService);
  private message = inject(MessageService);
  private translate = inject(TranslateService);

  public days = [
    { key: "monday", label: "common.weekdays.monday" },
    { key: "tuesday", label: "common.weekdays.tuesday" },
    { key: "wednesday", label: "common.weekdays.wednesday" },
    { key: "thursday", label: "common.weekdays.thursday" },
    { key: "friday", label: "common.weekdays.friday" },
    { key: "saturday", label: "common.weekdays.saturday" },
    { key: "sunday", label: "common.weekdays.sunday" },
  ];

  public isEnabledOptions: DropdownOption[] = [
    { label: "common.yes", value: true },
    { label: "common.no", value: false },
  ];

  public contactTypeOptions: DropdownOption[] = [
    {
      label: "gate_administration.open_times_form.primary",
      value: "PRIMARY",
    },
    {
      label: "gate_administration.open_times_form.additional",
      value: "ADDITIONAL",
    },
  ];

  public contactLangOptions: DropdownOption[] = [
    {
      label: "common.german",
      value: "de",
    },
    {
      label: "common.english",
      value: "en",
    },
  ];

  public timezoneOptions: DropdownOption[] = TIMEZONES.map((timezone) => ({
    label: timezone,
    value: timezone,
  }));

  private buildingId: string = this.config.data.buildingId;
  private actionType: "add" | "edit" = this.config.data.response.isExisting ? "edit" : "add";
  public buildingOpenTimesConfig: ThiefProtectionConfig = this.config.data.response.data;
  public gates = signal<AssetHierarchyDTO[]>(this.config.data.response.gates);

  public form!: FormGroup;

  /**
   * Returns the FormGroups for time windows of a given day
   * @param dayKey The day for which the time windows are to be returned
   * @returns An FormArray with FormGroups for all time windows of the given day
   */
  public getDayFormArray(dayKey: string) {
    return this.form.get(["dayTimewindows", dayKey]) as FormArray<FormGroup<TimeWindowFormGroup>>;
  }

  /**
   * Returns the FormArray for the contacts
   * @returns Returns an FormArray which contains FormGroups for all contacts
   */
  public getContactsArray() {
    return this.form.get("contacts") as FormArray<FormGroup<ContactFormGroup>>;
  }

  ngOnInit(): void {
    this.initForm();
  }

  /**
   * Initializes the Form with the values from the given Configuration
   */
  private initForm() {
    this.form = new FormGroup({
      isEnabled: new FormControl(this.buildingOpenTimesConfig.isEnabled),
      alarmBufferMinutes: new FormControl(this.buildingOpenTimesConfig.alarmBufferMinutes, {
        validators: [Validators.min(0), Validators.max(60), Validators.required],
        nonNullable: true,
      }),
      escalationIntervalMinutes: new FormControl(this.buildingOpenTimesConfig.escalationIntervalMinutes, {
        validators: [Validators.min(0), Validators.max(1440)],
      }),
      pushNotificationEnabled: new FormControl(this.buildingOpenTimesConfig.pushNotificationEnabled),
      timezone: new FormControl(this.buildingOpenTimesConfig.timezone),
      dayTimewindows: new FormGroup({
        monday: new FormArray<FormGroup<TimeWindowFormGroup>>([]),
        tuesday: new FormArray<FormGroup<TimeWindowFormGroup>>([]),
        wednesday: new FormArray<FormGroup<TimeWindowFormGroup>>([]),
        thursday: new FormArray<FormGroup<TimeWindowFormGroup>>([]),
        friday: new FormArray<FormGroup<TimeWindowFormGroup>>([]),
        saturday: new FormArray<FormGroup<TimeWindowFormGroup>>([]),
        sunday: new FormArray<FormGroup<TimeWindowFormGroup>>([]),
      }),
      contacts: new FormArray<FormGroup<ContactFormGroup>>([], {
        validators: [this.minArrayLength(1)],
      }),
    });
    this.populateTimeWindowControls();
    this.populateContactControls();
  }

  /**
   * Validator function to check for minimum length of an array
   * @param min The minimum count of elements the array needs to be valid
   * @returns A ValidatorFn that returns null for valid arrays or ValidationErrors with error details for invalid arrays
   */
  private minArrayLength(min: number): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value && control.value.length >= min) {
        return null;
      }
      return {
        minArrayLength: {
          requiredLength: min,
          actualLength: control.value?.length || 0,
        },
      };
    };
  }

  /**
   * Populates the FormArray for each day with FormGroups for all existing time windows
   */
  private populateTimeWindowControls() {
    for (const day of this.days) {
      const dayData = this.buildingOpenTimesConfig[day.key as keyof ThiefProtectionConfig] as Day | undefined;
      const timeWindows = dayData?.openTimeWindows ?? [];
      const dayFormArray = this.getDayFormArray(day.key);
      timeWindows.forEach((timeWindow) => {
        dayFormArray.push(
          this.createTimeWindowGroup(
            DateTime.fromFormat(timeWindow.startTime ?? "", "HH:mm").toJSDate(),
            DateTime.fromFormat(timeWindow.endTime ?? "", "HH:mm").toJSDate()
          )
        );
      });
    }
  }

  /**
   * Populates the FormArray for contacts with FormGroups for each existing contact
   */
  private populateContactControls() {
    const contactConfig: Contact[] = this.buildingOpenTimesConfig.contacts;
    const contactArray = this.getContactsArray();
    contactConfig.forEach((contact) => {
      contactArray.push(this.createContactGroup(contact));
    });
    if (contactArray.length === 0) {
      this.addContact();
    }
  }

  /**
   * Creates a FormGroup for a time window with initial values for startTime and endTime
   * @param startTime The initial value for the start time of the time window
   * @param endTime The initial value for the end time of the time window
   * @returns A FormGroup with a FormControl each for startTime and endTime with initial values
   */
  private createTimeWindowGroup(startTime: Date, endTime: Date) {
    return new FormGroup<TimeWindowFormGroup>({
      startTime: new FormControl<Date>(startTime, [Validators.required]),
      endTime: new FormControl<Date>(endTime, [Validators.required]),
    });
  }

  /**
   * Creates a FormGroup for a contact with initial values from a provided contact
   * @param contact The contact that is to be used for the initial values
   * @returns A FormGroup with a FormControl each for every property of a contact with initial values
   */
  private createContactGroup(contact: Contact) {
    return new FormGroup<ContactFormGroup>({
      email: new FormControl<string>(contact.email, {
        validators: [Validators.pattern(EMAIL_PATTERN), Validators.required],
        nonNullable: true,
      }),
      firstName: new FormControl<string>(contact.firstName, {
        validators: [Validators.required],
        nonNullable: true,
      }),
      lastName: new FormControl<string>(contact.lastName, {
        validators: [Validators.required],
        nonNullable: true,
      }),
      type: new FormControl<"PRIMARY" | "ADDITIONAL">(contact.type, {
        validators: [Validators.required],
        nonNullable: true,
      }),
      level: new FormControl<number>(contact.level, {
        validators: [Validators.min(1), Validators.max(10)],
        nonNullable: true,
      }),
      lang: new FormControl<"de" | "en">(contact.lang ?? "de", {
        validators: [Validators.required],
        nonNullable: true,
      }),
    });
  }

  /**
   * Adds a new time window FormGroup to a specified day
   * @param day The day to which a new time window is to be added
   */
  public addTimeWindow(day: string) {
    const dayArray = this.getDayFormArray(day);

    const lastTimeWindow = dayArray.at(dayArray.length - 1)?.value;

    const newStartTime = new Date();
    const newEndTime = new Date();

    if (lastTimeWindow?.endTime) {
      newStartTime.setHours(lastTimeWindow.endTime.getHours() + 1, 0);
      newEndTime.setHours(lastTimeWindow.endTime.getHours() + 2, 0);
    } else {
      newStartTime.setHours(7, 0);
      newEndTime.setHours(18, 0);
    }

    dayArray.push(this.createTimeWindowGroup(newStartTime, newEndTime));
  }

  /**
   * Removes a time window FormGroup from a day
   * @param day The day from which a time window is to be removed
   * @param index The index of the day to be removed
   */
  public deleteTimeWindow(day: string, index: number) {
    this.getDayFormArray(day).removeAt(index);
  }

  /**
   * Adds a new empty contact FormGroup to the contacts FormArray
   */
  public addContact() {
    const contactArray = this.getContactsArray();
    const contactGroup = this.createContactGroup(createDefaultContact());
    contactGroup.markAllAsDirty();
    contactArray.push(contactGroup);
  }

  /**
   * Removes a contact FormGroup from the contacts FormArray
   * @param index The index of the contact to be removed
   */
  public deleteContact(index: number) {
    this.getContactsArray().removeAt(index);
  }

  /**
   * Gets all values from the form and updates/creates the thief protection building.
   * If creating/updating was successful the dialog is closed.
   */
  public save() {
    if (this.form.valid) {
      const formValue = this.form.getRawValue();
      const thiefProtectionDays = this.extractTimeWindowsFromUI();
      const contacts = this.extractContactsFromUI();

      const basePayload = {
        isActive: formValue.isEnabled,
        alarmBufferMinutes: formValue.alarmBufferMinutes,
        escalationIntervalMinutes: formValue.escalationIntervalMinutes,
        pushNotificationEnabled: formValue.pushNotificationEnabled,
        timezone: formValue.timezone,
        contacts,
        ...thiefProtectionDays,
      };

      if (this.actionType === "add") {
        const createConfig: ThiefProtectionConfigCreate = basePayload;
        this.thiefProtectionService.createThiefProtectionConfig(this.buildingId, createConfig).subscribe({
          next: (response) => {
            if (response) {
              this.message.add({
                key: "global",
                severity: "success",
                summary: this.translate.instant("common.success"),
                detail: this.translate.instant("gate_administration.open_times_form.success"),
              });
              this.closeDialog();
            }
          },
        });
      } else {
        const updateConfig: ThiefProtectionConfigUpdate = {
          id: this.buildingOpenTimesConfig.id!,
          ...basePayload,
        };
        this.thiefProtectionService.updateThiefProtectionConfig(this.buildingId, updateConfig).subscribe({
          next: (response) => {
            if (response) {
              this.message.add({
                key: "global",
                severity: "success",
                summary: this.translate.instant("common.success"),
                detail: this.translate.instant("gate_administration.open_times_form.success"),
              });
              this.closeDialog();
            }
          },
        });
      }
    } else {
      this.message.add({
        key: "global",
        severity: "error",
        summary: this.translate.instant("common.error"),
        detail: this.translate.instant("common.invalid_form"),
        life: 4500,
      });
    }
  }

  /**
   * Closes the dialog
   */
  public closeDialog() {
    this.ref.close();
  }

  /**
   * Sets the secure state for a given gate system by creating or deleting a GateSecurityConfig
   * @param gateId The id of the gate whose configuration is to be activated/deactivated
   * @param newState The new state that the configuration should have. True creates a configuration, false deletes it.
   */
  public toggleSecurity(gateId: string, newState: boolean) {
    if (newState) {
      this.thiefProtectionService.activateGateSecurity(gateId).subscribe({
        next: (result) => {
          if (result) {
            this.message.add({
              key: "global",
              severity: "success",
              summary: this.translate.instant("common.success"),
              detail: this.translate.instant("gate_administration.open_times_form.gate_activation_success"),
            });
            this.refreshGateList();
          }
        },
      });
    } else {
      this.thiefProtectionService.deactivateGateSecurity(gateId).subscribe({
        next: (result) => {
          if (result) {
            this.message.add({
              key: "global",
              severity: "success",
              summary: this.translate.instant("common.success"),
              detail: this.translate.instant("gate_administration.open_times_form.gate_deactivation_success"),
            });
            this.refreshGateList();
          }
        },
      });
    }
  }

  /**
   * Reloads the list of gate systems and their security status
   */
  private refreshGateList() {
    this.thiefProtectionService.getGateSystemStatusesByBuilding(this.buildingId).subscribe({
      next: (res) => {
        if (res) {
          this.gates.set(res);
        }
      },
    });
  }

  /**
   * Gets the time window values from the form
   * @returns A Day-Object which contains an array with time windows for each day
   */
  private extractTimeWindowsFromUI() {
    const thiefProtectionDays: Record<string, Day> = {};
    for (const day of this.days) {
      const dayArray = this.getDayFormArray(day.key);
      if (dayArray.length > 0) {
        thiefProtectionDays[day.key] = {
          openTimeWindows: dayArray.controls
            .map((timeWindowControl) => ({
              startTime: timeWindowControl.value.startTime
                ? DateTime.fromJSDate(timeWindowControl.value.startTime).toFormat("HH:mm")
                : "",
              endTime: timeWindowControl.value.endTime
                ? DateTime.fromJSDate(timeWindowControl.value.endTime).toFormat("HH:mm")
                : "",
            }))
            .filter((timeWindow) => timeWindow.startTime && timeWindow.endTime),
        };
      }
    }
    return thiefProtectionDays;
  }

  /**
   * Gets every contacts values from the form
   * @returns An array which contains a ContactDTO object for each contact
   */
  private extractContactsFromUI() {
    const newContacts: ContactDTO[] = [];
    const contactArray = this.getContactsArray();
    contactArray.controls.forEach((control) => {
      const controlData = control.getRawValue();
      newContacts.push({
        contactType: controlData.type,
        contactValue: controlData.email,
        firstName: controlData.firstName,
        lastName: controlData.lastName,
        level: controlData.level,
        lang: controlData.lang,
      });
    });
    return newContacts;
  }
}
